import PropTypes from 'prop-types';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useStatisticsContext } from '../../../providers/StatisticsProvider';
import {
	SECTION_CHART_BUILDER,
	SECTION_ORDER_ANALYSIS,
	SECTION_STATISTICS_DASHBOARD,
} from '../common/lib/statisticsPageSections';
import { useOrderPrimaryPhaseStatsState } from '../charts/pipeline/hooks/useOrderPrimaryPhaseStatsState';

const StatisticsPageContext = createContext(null);

const scrollToSectionId = (sectionId) => {
	requestAnimationFrame(() => {
		document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
	});
};

export const StatisticsPageProvider = ({ children }) => {
	const statsState = useOrderPrimaryPhaseStatsState();
	const { applyStatisticsFilters, statisticsFiltersDraft } = useStatisticsContext();
	const [chartDetailModalChartId, setChartDetailModalChartId] = useState(null);
	const [chartLibraryModalOpen, setChartLibraryModalOpen] = useState(false);
	const [chartWorkbenchModalOpen, setChartWorkbenchModalOpen] = useState(false);
	const [chartWorkbenchSessionKey, setChartWorkbenchSessionKey] = useState(0);
	const [chartWorkbenchEditChartId, setChartWorkbenchEditChartId] = useState(null);
	const scrollToSection = useCallback((sectionId) => {
		scrollToSectionId(sectionId);
	}, []);

	const openChartDetailModal = useCallback((chartId) => {
		if (typeof chartId === 'string' && chartId) {
			setChartWorkbenchModalOpen(false);
			setChartWorkbenchEditChartId(null);
			setChartDetailModalChartId(chartId);
		}
	}, []);

	const closeChartDetailModal = useCallback(() => {
		setChartDetailModalChartId(null);
	}, []);

	const openChartLibraryModal = useCallback(() => {
		setChartWorkbenchModalOpen(false);
		setChartWorkbenchEditChartId(null);
		setChartDetailModalChartId(null);
		setChartLibraryModalOpen(true);
	}, []);

	const closeChartLibraryModal = useCallback(() => {
		setChartLibraryModalOpen(false);
	}, []);

	/** 打开 Explorer L2；传入 chartId 为编辑已保存图，关闭后回到 L1 */
	const openChartWorkbenchModal = useCallback((chartId) => {
		setChartWorkbenchSessionKey((key) => key + 1);
		const editId = typeof chartId === 'string' && chartId ? chartId : null;
		setChartWorkbenchEditChartId(editId);
		setChartDetailModalChartId(editId);
		setChartLibraryModalOpen(true);
		setChartWorkbenchModalOpen(true);
	}, []);

	const closeChartWorkbenchModal = useCallback(() => {
		setChartWorkbenchModalOpen(false);
		setChartWorkbenchEditChartId(null);
	}, []);

	const handleGoToDashboard = useCallback(() => {
		scrollToSection(SECTION_STATISTICS_DASHBOARD);
	}, [scrollToSection]);

	const handleGoToOrderAnalysis = useCallback(() => {
		scrollToSection(SECTION_ORDER_ANALYSIS);
	}, [scrollToSection]);

	const handleGoToReportBuilder = useCallback(() => {
		scrollToSection(SECTION_CHART_BUILDER);
	}, [scrollToSection]);

	const handleCompareDisplayChange = useCallback(
		(patch) => {
			applyStatisticsFilters({ ...statisticsFiltersDraft, ...patch });
		},
		[applyStatisticsFilters, statisticsFiltersDraft],
	);

	const value = useMemo(
		() => ({
			statsState,
			chartDetailModalChartId,
			chartLibraryModalOpen,
			chartWorkbenchModalOpen,
			chartWorkbenchSessionKey,
			chartWorkbenchEditChartId,
			openChartDetailModal,
			closeChartDetailModal,
			openChartLibraryModal,
			closeChartLibraryModal,
			openChartWorkbenchModal,
			closeChartWorkbenchModal,
			handleGoToDashboard,
			handleGoToOrderAnalysis,
			handleGoToReportBuilder,
			handleCompareDisplayChange,
		}),
		[
			chartDetailModalChartId,
			chartLibraryModalOpen,
			chartWorkbenchModalOpen,
			chartWorkbenchSessionKey,
			chartWorkbenchEditChartId,
			closeChartDetailModal,
			closeChartLibraryModal,
			closeChartWorkbenchModal,
			handleCompareDisplayChange,
			handleGoToDashboard,
			handleGoToOrderAnalysis,
			handleGoToReportBuilder,
			openChartDetailModal,
			openChartLibraryModal,
			openChartWorkbenchModal,
			statsState,
		],
	);

	return <StatisticsPageContext.Provider value={value}>{children}</StatisticsPageContext.Provider>;
};

StatisticsPageProvider.propTypes = {
	children: PropTypes.node.isRequired,
};

export const useStatisticsPageContext = () => {
	const context = useContext(StatisticsPageContext);
	if (!context) {
		throw new Error('useStatisticsPageContext must be used within StatisticsPageProvider');
	}
	return context;
};

export const useStatisticsStatsState = () => useStatisticsPageContext().statsState;

/** 经营看板外（如首页）调用时返回 null，避免抛错 */
export const useOptionalStatisticsStatsState = () => {
	const context = useContext(StatisticsPageContext);
	return context?.statsState ?? null;
};

export default StatisticsPageProvider;
